/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  BookModel,
  ImagePosition,
  PageCanvasModel,
  PageIcon,
  PageIconType,
  PageItemCanvasModel,
  PageItemModel,
  SbPageItemFile,
  SbPageItemType,
} from "@/types";
import { SvgIconKey, scaleConfig } from "@/@corefabriccanvas/icons";
import { getUserNotes } from "./userNoteDatabaseService";
import { isValidURL, updateOrderBasedOnIndex } from "@/utility/utils";
import { Type } from "@/@corefabriccanvas/exercises/utils";
import { apiConfig } from "@/configs/appConfig";
import { get, remove, set } from "@/data/ionicStorage";

const constants = {
  yMargin: 50,
  iconTopMargin: 0,
  iconLeftMargin: 40,
  showAnswerIconTopMargin: 100,
  testToolHeight: 80,
  questionFileTopMargin: 50,
};

// Initial default value for server-side rendering
let questionBaseLeftPosition = 0;

// This function will be used in client components via useEffect
export function initializeBasePosition(): void {
  if (typeof window !== "undefined") {
    try {
      const navigator = document.getElementsByClassName("side-tools")[0];
      const pageLink = document.getElementsByClassName("page-link-btn")[0];
      if (pageLink) {
        questionBaseLeftPosition = (pageLink as HTMLElement).offsetWidth;
      } else if (navigator) {
        questionBaseLeftPosition = (navigator as HTMLElement).offsetWidth;
      }
    } catch (error) {
      console.error("Error accessing DOM elements:", error);
    }
  }
}

// Get the current position value (can be called anytime)
export function getBaseLeftPosition(): number {
  return questionBaseLeftPosition;
}

// Types & Interfaces for Abstraction
interface PageImage {
  imagePath: string;
  width: number;
  height: number;
}

interface IconConfig {
  ratio: number;
  canvasHeight: number;
  canvasWidth: number;
  pagePath: PageImage;
  bookId?: number;
  pageItem?: PageItemModel;
}

class RatioCalculator {
  static calculateRatio(
    canvasWidth: number,
    canvasHeight: number,
    imageWidth: number,
    imageHeight: number
  ): number {
    return Math.min(canvasWidth / imageWidth, canvasHeight / imageHeight);
  }
}

class IconMapper {
  private static iconTypeMap: Record<PageIconType, SvgIconKey> = {
    [PageIconType.Question]: SvgIconKey.Magnify,
    [PageIconType.ShowQuestionAnswer]: SvgIconKey.ShowAnswer,
    [PageIconType.QuestionAnswer]: SvgIconKey.QuestionAnswerIcon,
    [PageIconType.ShowGapFillingItem]: SvgIconKey.Eye,
    [PageIconType.CloseGapFillingItem]: SvgIconKey.EyeClose,
    [PageIconType.Note]: SvgIconKey.SoundIcon,
    [PageIconType.Sound]: SvgIconKey.SoundIcon,
    [PageIconType.Video]: SvgIconKey.SoundIcon,
    [PageIconType.Url]: SvgIconKey.Link,
    [PageIconType.Html]: SvgIconKey.Link,
    [PageIconType.Image]: SvgIconKey.Image,
    [PageIconType.Games]: SvgIconKey.InteractionIcon,
    [PageIconType.Interacts]: SvgIconKey.InteractionIcon,
    [PageIconType.TextNote]: SvgIconKey.Text,
    [PageIconType.Drawing]: SvgIconKey.Drawing,
    [PageIconType.File]: SvgIconKey.File,
  };

  static getIconType(type: PageIconType): SvgIconKey {
    return this.iconTypeMap[type] || SvgIconKey.Magnify;
  }

  static async mapItemsToIcons(
    items: PageItemModel[],
    config: IconConfig,
    pageIconType: PageIconType
  ): Promise<PageIcon[]> {
    return await Promise.all(
      items.map((item) => this.transformItemToIcon(item, config, pageIconType))
    );
  }

  private static async transformItemToIcon(
    item: PageItemModel,
    config: IconConfig,
    pageIconType: PageIconType
  ): Promise<PageIcon> {
    let { imageWidth, imageHeight } = item.position || {
      imageWidth: 0,
      imageHeight: 0,
    };
    if (imageWidth === 0 || config.pageItem?.position) {
      imageWidth = config.pageItem?.position?.imageWidth ?? 0;
    }
    if (imageHeight === 0 || config.pageItem?.position) {
      imageHeight = config.pageItem?.position?.imageHeight ?? 0;
    }
    const position = item.position || {
      width: 0,
      height: 0,
      axisX: 0,
      axisY: 0,
      imageWidth: imageWidth,
      imageHeight: imageHeight,
    };
    const imageRatio = RatioCalculator.calculateRatio(
      config.pagePath.width,
      config.pagePath.height,
      position.imageWidth != 0 ? position.imageWidth : imageWidth,
      position.imageHeight != 0 ? position.imageHeight : imageHeight
    );
    console.log("imageRatio", imageRatio);
    const ratio = config.ratio * imageRatio;
    let scale =
      config.canvasHeight /
      849 /
      (scaleConfig[this.getIconType(pageIconType)] || 1);

    if (config.pagePath.width > config.pagePath.height) {
      scale =
        config.canvasWidth /
        849 /
        (scaleConfig[this.getIconType(pageIconType)] || 1);
    }

    let filePath = "";

    if (
      item.pageItemType === SbPageItemType.Sound ||
      item.pageItemType === SbPageItemType.Video
    ) {
      filePath = await PageCanvasService.getActivityPath(
        config.bookId || 0,
        item.id,
        item
      );
    }
    return {
      entityId: item.id,
      x: position.axisX * ratio,
      y: position.axisY * ratio,
      leftMargin: constants.iconLeftMargin * ratio,
      topMargin:
        constants.iconTopMargin * ratio +
        (pageIconType === PageIconType.ShowQuestionAnswer
          ? constants.showAnswerIconTopMargin * ratio
          : 0),
      width: position.width * ratio,
      height: position.height * ratio,
      pageNumber: item.pageNumber,
      type: pageIconType,
      correctAnswer: item.correctAnswer || "",
      icon: this.getIconType(pageIconType),
      iconScale: scale,
      id: this.getIconId(item, pageIconType),
      bookId: config.bookId,
      questionId: item.questionId,
      transcript: item.transcript,
      filePath: filePath,
      isExternalLink: false,
      activityLink: item.activityLink,
      hasSecondLayer: item.secondLayer ? true : false,
      gapFillingOptionImage: item.imageUrl,
      secondLayer: item.secondLayer,
    };
  }

  private static getIconId(
    item: PageItemModel,
    pageIconType: PageIconType
  ): string {
    switch (pageIconType) {
      case PageIconType.ShowGapFillingItem:
        return `${item.id}-gapFillingOption-show`;
      case PageIconType.CloseGapFillingItem:
        return `${item.id}-gapFillingOption-close`;
      case PageIconType.ShowQuestionAnswer:
        return `${item.id}-showQuestionAnswer`;
      case PageIconType.QuestionAnswer:
        return `${item.id}-questionAnswer`;
      default:
        return `${item.id}`;
    }
  }

  // gapfilling ve exerciselar için
  static async mapPageItemsIcons(
    items: PageItemModel[],
    config: IconConfig,
    pageIconType: PageIconType
  ): Promise<PageIcon[]> {
    return await Promise.all(
      items.map((item) =>
        this.transformPageItemIcon(item, config, pageIconType)
      )
    );
  }

  private static async transformPageItemIcon(
    item: PageItemModel,
    config: IconConfig,
    pageIconType: PageIconType
  ): Promise<PageIcon> {
    // Varsayılan değerler
    const defaultPosition = {
      width: 0,
      height: 0,
      axisX: 0,
      axisY: 0,
    };

    const position = { ...defaultPosition, ...item.position };

    // Ölçek hesaplama
    let scale =
      config.canvasHeight /
      849 /
      (scaleConfig[this.getIconType(pageIconType)] || 1);

    if (config.pagePath.width > config.pagePath.height) {
      scale =
        config.canvasWidth /
        849 /
        (scaleConfig[this.getIconType(pageIconType)] || 1);
    }
    // Dosya yolu belirleme
    const filePath =
      item.pageItemType === SbPageItemType.Sound ||
      item.pageItemType === SbPageItemType.Video
        ? await PageCanvasService.getActivityPath(
            config.bookId || 0,
            item.id,
            item
          )
        : "";

    // İkon objesi oluşturma
    return {
      entityId: item.id,
      x: position.axisX * config.ratio,
      y: position.axisY * config.ratio,
      leftMargin: constants.iconLeftMargin * config.ratio,
      topMargin:
        constants.iconTopMargin * config.ratio +
        (pageIconType === PageIconType.ShowQuestionAnswer ||
        pageIconType === PageIconType.QuestionAnswer
          ? constants.showAnswerIconTopMargin * config.ratio
          : 0),
      width: position.width * config.ratio,
      height: position.height * config.ratio,
      pageNumber: item.pageNumber,
      type: pageIconType,
      correctAnswer: item.correctAnswer || "",
      icon: this.getIconType(pageIconType),
      iconScale: scale,
      id: this.getIconId(item, pageIconType),
      bookId: config.bookId,
      questionId: item.questionId,
      targets: item.targets,
      group: item.group,
      isCorrect: item.isCorrect,
      transcript: item.transcript,
      filePath,
      isExternalLink: false,
      activityLink: item.link,
      gapFillingOptionImage: item.imageUrl,
      secondLayer: item.secondLayer,
    };
  }
}

class PageItemService {
  static categorizeItems(pageItems: PageItemModel[]) {
    const questions = pageItems.filter((item) =>
      [
        SbPageItemType.MultipleChoice,
        SbPageItemType.Subject,
        SbPageItemType.GapFilling,
      ].includes(item.pageItemType)
    );
    const interacts = pageItems.filter((item) =>
      [
        SbPageItemType.DragDrop,
        SbPageItemType.TrueFalse,
        SbPageItemType.Matching,
        SbPageItemType.FindCorrectOne,
        SbPageItemType.MarkObject,
        SbPageItemType.Tick,
        SbPageItemType.Underline,
        SbPageItemType.OpenEnded,
      ].includes(item.pageItemType)
    );

    const sounds = pageItems.filter((item) =>
      [SbPageItemType.Sound].includes(item.pageItemType)
    );

    const videos = pageItems.filter((item) =>
      [SbPageItemType.Video].includes(item.pageItemType)
    );
    const games = pageItems.filter((item) =>
      [SbPageItemType.Game].includes(item.pageItemType)
    );
    const links = pageItems.filter((item) =>
      [SbPageItemType.Link].includes(item.pageItemType)
    );

    const urls = pageItems.filter((item) =>
      [SbPageItemType.Html].includes(item.pageItemType)
    );
    const multipleChoiceQuestions = pageItems.filter(
      (item) =>
        item.pageItemType === SbPageItemType.MultipleChoice &&
        item.correctAnswer
    );

    const gapFillingOptions = this.extractGapFillingOptions(pageItems);

    return {
      questions,
      interacts,
      sounds,
      videos,
      games,
      links,
      urls,
      multipleChoiceQuestions,
      gapFillingOptions,
    };
  }

  private static extractGapFillingOptions(
    pageItems: PageItemModel[]
  ): PageItemModel[] {
    return pageItems
      .filter((item) => item.pageItemType === SbPageItemType.GapFilling)
      .flatMap((item) =>
        item.options.map((option) => ({
          ...option,
          questionId: item.id,
          position: option.position
            ? {
                ...option.position,
                imageWidth:
                  option.position.imageWidth === 0
                    ? (item.position?.imageWidth ?? 0)
                    : option.position.imageWidth,
                imageHeight:
                  option.position.imageHeight === 0
                    ? (item.position?.imageHeight ?? 0)
                    : option.position.imageHeight,
              }
            : undefined,
        }))
      );
  }
}

export class PageCanvasService {
  static async getBookPageByNumber(
    book: BookModel,
    pageNo: number,
    canvasWidth: number,
    canvasHeight: number,
    userId: string
  ): Promise<PageCanvasModel | undefined> {
    const currentPage = book.pages.find((x) => x.pageNo === pageNo);
    if (!currentPage) return undefined;
    await set(`${book.id}-lastPage`, pageNo.toString());
    const pagePath = await PageCanvasService.getImageSize(
      currentPage.pageImageUrl
    );
    const ratio = RatioCalculator.calculateRatio(
      canvasWidth,
      canvasHeight,
      pagePath.width,
      pagePath.height
    );

    const categorizedItems = PageItemService.categorizeItems(
      currentPage.pageItems
    );
    const pageIcons = [
      ...(await IconMapper.mapItemsToIcons(
        categorizedItems.questions,
        { ratio, canvasHeight, canvasWidth, pagePath },
        PageIconType.Question
      )),
      ...(await IconMapper.mapItemsToIcons(
        categorizedItems.interacts,
        { ratio, canvasHeight, canvasWidth, pagePath },
        PageIconType.Interacts
      )),
      ...(await IconMapper.mapItemsToIcons(
        categorizedItems.sounds,
        { ratio, bookId: book.id, canvasHeight, canvasWidth, pagePath },
        PageIconType.Sound
      )),
      ...(await IconMapper.mapItemsToIcons(
        categorizedItems.videos,
        { ratio, bookId: book.id, canvasHeight, canvasWidth, pagePath },
        PageIconType.Video
      )),
      ...(await IconMapper.mapItemsToIcons(
        categorizedItems.games,
        { ratio, bookId: book.id, canvasHeight, canvasWidth, pagePath },
        PageIconType.Games
      )),
      ...(await IconMapper.mapItemsToIcons(
        categorizedItems.urls,
        { ratio, canvasHeight, canvasWidth, pagePath },
        PageIconType.Url
      )),
      ...(await IconMapper.mapItemsToIcons(
        categorizedItems.multipleChoiceQuestions,
        { ratio, canvasHeight, canvasWidth, pagePath, bookId: book.id },
        PageIconType.ShowQuestionAnswer
      )),
      ...(await IconMapper.mapItemsToIcons(
        categorizedItems.multipleChoiceQuestions,
        { ratio, canvasHeight, canvasWidth, pagePath, bookId: book.id },
        PageIconType.QuestionAnswer
      )),
      ...(await IconMapper.mapItemsToIcons(
        categorizedItems.gapFillingOptions,
        { ratio, canvasHeight, canvasWidth, pagePath, bookId: book.id },
        PageIconType.ShowGapFillingItem
      )),
      ...(await IconMapper.mapItemsToIcons(
        categorizedItems.gapFillingOptions,
        { ratio, canvasHeight, canvasWidth, pagePath, bookId: book.id },
        PageIconType.CloseGapFillingItem
      )),
      ...(await getUserNotes(
        book.id,
        currentPage.id,
        "null",
        userId,
        canvasWidth,
        canvasHeight
      )),
    ];
    const pageItems = currentPage.pageItems.filter(
      (x) =>
        x.pageItemType !== SbPageItemType.Game &&
        x.pageItemType !== SbPageItemType.Html &&
        x.pageItemType !== SbPageItemType.Video &&
        x.pageItemType !== SbPageItemType.Sound &&
        x.pageItemType !== SbPageItemType.Link &&
        x.pageItemType !== SbPageItemType.PageLink &&
        x.pageItemType !== SbPageItemType.Painting
    );
    return {
      pageId: currentPage.id,
      pageImage: pagePath.imagePath,
      pageNo: currentPage.pageNo,
      imageWidth: pagePath.width,
      imageHeight: pagePath.height,
      canvasWidth,
      canvasHeight,
      ratio,
      pageItems: updateOrderBasedOnIndex(pageItems),
      pageIcons,
    };
  }

  static async defineExerciseType(sources: PageIcon[]) {
    // Kaynakları filtrele
    // Tüm hedefleri çıkar
    const targets = sources.filter((x) => x.targets).flatMap((x) => x.targets);
    // Birden fazla hedefi olan kaynakları bul
    const manyTargetsSources = sources.filter(
      (x) => (x.targets?.length ?? 0) > 1
    );

    // Benzersiz hedefleri bul
    const uniqueTargets = Array.from(new Set(targets.map((t) => t?.id))).map(
      (id) => targets.find((t) => t?.id === id)
    );

    // Hedef türünü belirle
    if (targets.length !== uniqueTargets.length) {
      return Type.ManyToMany;
    }

    if (manyTargetsSources.length === 0) {
      return Type.OneToOne;
    }

    if (targets.length === uniqueTargets.length) {
      return Type.OneToMany;
    }
  }

  static getImageSize = (url: string): Promise<PageImage> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        resolve({ imagePath: url, width: img.width, height: img.height });
      };
      img.onerror = (err) => {
        reject(err);
      };
      img.src = url;
    });
  };

  static async getPageItemFromId(
    bookId: number,
    canvasWidth: number,
    canvasHeight: number,
    currentPage: PageCanvasModel,
    pageItemId: number
  ): Promise<PageItemCanvasModel | null> {
    try {
      const pageItem = currentPage.pageItems.find(
        (item) => item.id === pageItemId
      );
      console.log(pageItem);

      if (!pageItem || !pageItem.position) return null;

      const { width, height, axisX, axisY } = pageItem.position;

      const pagePath = await PageCanvasService.getImageSize(
        currentPage?.pageImage
      );

      let backgroundImage = "";
      let backgroundImageScale = 1;
      const questionBackground = pageItem.files?.filter(
        (x) => x.fileType === 40
      );
      if (questionBackground && questionBackground.length > 0) {
        backgroundImage = questionBackground[0].fileUrl;
        const backgroundImageSize =
          await PageCanvasService.getImageSize(backgroundImage);
        backgroundImageScale = RatioCalculator.calculateRatio(
          canvasWidth - questionBaseLeftPosition - constants.iconLeftMargin,
          canvasHeight - constants.testToolHeight,
          backgroundImageSize.width,
          backgroundImageSize.height
        );
      }

      let imageRatio = RatioCalculator.calculateRatio(
        pagePath.width,
        pagePath.height,
        pageItem.position.imageWidth,
        pageItem.position.imageHeight
      );
      let ratio = RatioCalculator.calculateRatio(
        canvasWidth - questionBaseLeftPosition - constants.iconLeftMargin,
        canvasHeight - constants.testToolHeight,
        width * imageRatio,
        height * imageRatio
      );
      if (backgroundImage) {
        ratio = backgroundImageScale;
        imageRatio = 1;
      }

      if (pageItem.rootPosition) {
        ratio = RatioCalculator.calculateRatio(
          canvasWidth - questionBaseLeftPosition - constants.iconLeftMargin,
          canvasHeight - constants.testToolHeight,
          (pageItem.rootPosition.width + width) * imageRatio,
          height * imageRatio
        );
      }
      let sources: PageIcon[] = [];
      let targets: PageIcon[] = [];
      let gapFillingIcons: PageIcon[] = []; //
      let closeGapFillingIcons: PageIcon[] = []; //
      const iconRatio = ratio / currentPage.ratio;

      if (pageItem.pageItemType === SbPageItemType.GapFilling) {
        gapFillingIcons = await IconMapper.mapPageItemsIcons(
          pageItem.options,
          {
            ratio: ratio * imageRatio,
            canvasHeight,
            canvasWidth,
            pagePath,
            bookId,
            pageItem,
          },
          PageIconType.ShowGapFillingItem
        );
        gapFillingIcons.map((i) => {
          i.x = i.x - axisX * ratio * imageRatio;
          i.y = i.y - axisY * ratio * imageRatio;
          i.questionId = pageItem.id;
          i.iconScale = i.iconScale * iconRatio;
        });

        closeGapFillingIcons = await IconMapper.mapPageItemsIcons(
          pageItem.options,
          {
            ratio: ratio * imageRatio,
            canvasHeight,
            canvasWidth,
            pagePath,
            bookId,
            pageItem,
          },
          PageIconType.CloseGapFillingItem
        );
        closeGapFillingIcons.map((i) => {
          i.x = i.x - axisX * ratio * imageRatio;
          i.y = i.y - axisY * ratio * imageRatio;
          i.questionId = pageItem.id;
          i.iconScale = i.iconScale * iconRatio;
        });
      }

      const isPageItemExercise = PageCanvasService.isExercise(pageItem);
      let exerciseType: any = undefined;
      //Eğer Aktivite ise
      if (isPageItemExercise) {
        console.log(imageRatio);
        sources = await IconMapper.mapPageItemsIcons(
          pageItem.options.filter((x) => x.isTarget === false),
          {
            ratio: ratio * imageRatio,
            canvasHeight,
            canvasWidth,
            pagePath,
            bookId,
            pageItem,
          },
          PageIconType.Question
        );
        sources.map((i) => {
          i.x = i.x - axisX * ratio * imageRatio;
          i.y = i.y - axisY * ratio * imageRatio;
          i.questionId = pageItem.id;
          i.iconScale = i.iconScale * iconRatio;
        });
        targets = await IconMapper.mapPageItemsIcons(
          pageItem.options.filter((x) => x.isTarget === true),
          {
            ratio: ratio * imageRatio,
            canvasHeight,
            canvasWidth,
            pagePath,
            bookId,
            pageItem,
          },
          PageIconType.Question
        );
        targets.map((i) => {
          i.x = i.x - axisX * ratio * imageRatio;
          i.y = i.y - axisY * ratio * imageRatio;
          i.questionId = pageItem.id;
          i.iconScale = i.iconScale * iconRatio;
        });
        exerciseType = await this.defineExerciseType(sources);
      }
      let files: PageIcon[] = [];

      const iconScale =
        canvasHeight / 849 / (scaleConfig[SvgIconKey.SoundIcon] || 1);

      if (pageItem.files && pageItem.files.length > 0) {
        files = await Promise.all(
          pageItem.files
            .filter((x) => x.fileType !== 40)
            .map(async (file) => ({
              entityId: file.id,
              x:
                file.position.axisX * ratio * imageRatio -
                axisX * ratio * imageRatio,
              y:
                file.position.axisY * ratio * imageRatio -
                axisY * ratio * imageRatio +
                constants.yMargin * ratio,
              leftMargin: 0,
              topMargin: 0,
              width: 0,
              height: 0,
              pageNumber: currentPage.pageNo,
              type: PageIconType.Sound,
              correctAnswer: "",
              icon: SvgIconKey.SoundIcon,
              iconScale: iconScale * iconRatio,
              id: `${file.id}`,
              bookId: bookId,
              questionId: pageItem.id,
              transcript: file.transcript,
              filePath: await PageCanvasService.getQuestionFilePath(
                bookId,
                pageItemId,
                file
              ),
              isExternalLink: file.isExternalLink,
              activityLink: file.fileUrl,
              ratio: 1,
              gapFillingOptionImage: "",
            }))
        );
      }
      return {
        id: pageItem.id,
        backgroundImage: backgroundImage,
        backgroundImageScale: backgroundImageScale,
        previousId: PageCanvasService.findPreviousId(currentPage, pageItemId),
        nextId: PageCanvasService.findNextId(currentPage, pageItemId),
        pageId: currentPage.pageId,
        pageNo: currentPage.pageNo,
        pageImage: currentPage.pageImage,
        canvasWidth,
        canvasHeight,
        imageWidth: pagePath.width,
        imageHeight: pagePath.height,
        baseLeftPosition: questionBaseLeftPosition,
        ratio,
        xInCanvas: pageItem.position.axisX * imageRatio,
        yInCanvas: pageItem.position.axisY * imageRatio,
        widthInCanvas: pageItem.position.width * imageRatio,
        heightInCanvas: pageItem.position.height * imageRatio,
        pageIcons: [...gapFillingIcons, ...closeGapFillingIcons, ...files],
        correctAnswer: pageItem.correctAnswer,
        solutionImage: pageItem.solutionImage,
        solutionVideo: pageItem.solutionVideo,
        solutionVideoId: pageItem.solutionVideoId,
        sources: sources,
        targets: targets,
        isExercise: isPageItemExercise,
        exerciseType: exerciseType,
        rootPosition: PageCanvasService.calculateRootPosition(
          pageItem,
          imageRatio
        ),
        rootPageNo: pageItem.rootPosition?.page || 0,
        order: pageItem.order,
        pageItemType: pageItem.pageItemType,
        hasCompleteButton: pageItem.setting?.hasCompleteButton,
        customOptions: pageItem.setting?.customOptions,
        secondLayer: pageItem.secondLayer,
      };
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  static async getActivityPath(
    bookId: number,
    questionId: number,
    pageItem: any
  ): Promise<string> {
    const BaseAddress = `${apiConfig.baseWeb}/Media/UserData/`;

    const { isExternalLink, activityLink } = pageItem;
    const fullActivityLink = `${BaseAddress}${activityLink || ""}`;

    if (!isExternalLink && !isValidURL(activityLink || "")) {
      if (isValidURL(activityLink)) {
        return activityLink;
      } else if (isValidURL(fullActivityLink)) {
        return fullActivityLink;
      }
    } else if (isValidURL(activityLink || "")) {
      return activityLink || "";
    }

    return "";
  }

  static async getQuestionFilePath(
    bookId: number,
    questionId: number,
    file: SbPageItemFile
  ): Promise<string> {
    return file.fileUrl;
  }
  private static isExercise(currentPageItem: PageItemModel): boolean {
    return (
      currentPageItem.pageItemType === SbPageItemType.DragDrop ||
      currentPageItem.pageItemType === SbPageItemType.MarkObject ||
      currentPageItem.pageItemType === SbPageItemType.FindCorrectOne ||
      currentPageItem.pageItemType === SbPageItemType.Tick ||
      currentPageItem.pageItemType === SbPageItemType.Underline ||
      currentPageItem.pageItemType === SbPageItemType.OpenEnded ||
      currentPageItem.pageItemType === SbPageItemType.TrueFalse ||
      currentPageItem.pageItemType === SbPageItemType.Matching ||
      currentPageItem.pageItemType === SbPageItemType.Painting
    );
  }
  private static findPreviousId(
    currentPage: PageCanvasModel,
    pageItemId: number
  ): number | undefined {
    const index = currentPage.pageItems.findIndex(
      (item) => item.id === pageItemId
    );
    return index > 0 ? currentPage.pageItems[index - 1].id : undefined;
  }

  private static findNextId(
    currentPage: PageCanvasModel,
    pageItemId: number
  ): number | undefined {
    const index = currentPage.pageItems.findIndex(
      (item) => item.id === pageItemId
    );
    return index < currentPage.pageItems.length - 1
      ? currentPage.pageItems[index + 1].id
      : undefined;
  }

  private static calculateRootPosition(
    pageItem: PageItemModel,
    imageRatio: number
  ): ImagePosition | null {
    return pageItem.rootPosition
      ? {
          xInCanvas: pageItem.rootPosition.axisX * imageRatio,
          yInCanvas: pageItem.rootPosition.axisY * imageRatio,
          widthInCanvas: pageItem.rootPosition.width * imageRatio,
          heightInCanvas: pageItem.rootPosition.height * imageRatio,
        }
      : null;
  }
}

export const getQuestionAnswerBtnId = (questionId: number) =>
  `${questionId}-questionAnswer`;
export const getShowQuestionAnswerBtnId = (questionId: number) =>
  `${questionId}-showQuestionAnswer`;

export const getShowGapFillingBtnId = (questionId: number) =>
  `${questionId}-gapFillingOption-show`;
export const getCloseGapFillingBtnId = (questionId: number) =>
  `${questionId}-gapFillingOption-close`;

export const saveUserPageDrawings = async (
  bookId: string | number,
  displayType: string,
  itemId: string | number,
  drawing: any
): Promise<void> => {
  const key = `${bookId}-${displayType}-${itemId}`;
  await remove(key);
  await set(key, drawing);
};

export const getUserPageDrawings = async (
  bookId: string | number,
  displayType: string,
  itemId: string | number
): Promise<any> => {
  const key = `${bookId}-${displayType}-${itemId}`;
  return await get(key);
};

export const removeUserDrawing = async (
  bookId: string | number,
  displayType: string,
  itemId: string | number
): Promise<any> => {
  const key = `${bookId}-${displayType}-${itemId}`;
  await remove(key);
};
